//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import {
  Control,
  FormProvider,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
  useForm,
} from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import {
  useUploadBrandBriefMutation,
  useUploadInspirationImageMutation,
  useUploadInspirationVideoMutation,
} from '@apis/campaign-requests/sampling/campaign-requests/samplingCampaignRequestUploadQueries';
import {
  Retailer,
  SamplingCampaignRequest,
} from '@apis/campaign-requests/sampling/campaign-requests/samplingCampaignRequestsApiModel';
import { BVSamplingProduct } from '@apis/campaign-requests/sampling/products/samplingProductsApiModel';
import { QuoteState } from '@apis/campaign-requests/sampling/quote/samplingQuoteApiModel';
import {
  ClientRetailerWithId,
  GetClientRetailersResponse,
} from '@apis/campaign-requests/sampling/retailers/samplingRetailersApiModel';
import { useSearchVibeCommunitiesQuery } from '@apis/campaign-requests/sampling/vibe-communities/vibeCommunitiesQueries';
import {
  useDeleteClientCampaignRequestMutation,
  useEditClientCampaignRequestMutation,
  useGetClientRetailersQuery,
  usePatchQuoteStatusMutation,
  useRequestQuoteMutation,
  useSamplingCampaignRequestQuery,
} from '@campaigns/components/campaign-requests/sampling-campaigns/SamplingCampaignsQueries';
import { SamplingCampaignRequestParams } from '@campaigns/components/campaign-requests/sampling-campaigns/SamplingCampaignsTypes';
import { ChangePurchaseOptionModal } from '@campaigns/components/campaign-requests/sampling-campaigns/edit/prepaid/modals/ChangePurchaseOptionModal/ChangePurchaseOptionModal';
import { PaymentSuccessModal } from '@campaigns/components/campaign-requests/sampling-campaigns/edit/prepaid/modals/PaymentSuccessModal';
import { RequestNewPurchaseCampaignModal } from '@campaigns/components/campaign-requests/sampling-campaigns/edit/prepaid/modals/RequestNewPurchaseCampaignModal';
import { RequestPrepaidCampaignModal } from '@campaigns/components/campaign-requests/sampling-campaigns/edit/prepaid/modals/RequestPrepaidCampaignModal';
import { CreateRedemptionLocationModal } from '@campaigns/components/campaign-requests/sampling-campaigns/edit/shared/fulfillment-section/deals/create-redemption-location/CreateRedemptionLocationModal';
import { RequestContactModal } from '@campaigns/components/campaign-requests/sampling-campaigns/edit/shared/modals/RequestContactModal';
import { BulkUploadModal } from '@campaigns/components/campaign-requests/sampling-campaigns/edit/shared/products-section/bulk-upload-modal/BulkUploadModal';
import { RequestCampaignModal } from '@campaigns/components/campaign-requests/sampling-campaigns/edit/standard/RequestCampaignModal';
import {
  RequestCampaignSuccessModal,
  RequestCampaignSuccessModalVariant,
} from '@campaigns/components/campaign-requests/sampling-campaigns/edit/standard/RequestCampaignSuccessModal';
import { SamplingRequestSubmitErrorsModal } from '@campaigns/components/campaign-requests/sampling-campaigns/edit/standard/SamplingRequestSubmitErrorsModal';
import { isSupportedMimeType } from '@campaigns/components/shared/file-uploader/utils';
import { CONTENT_INSPIRATION_VIDEO_MIME_TYPES } from '@campaigns/constants/sampling/SamplingCampaignRequestConstants';
import { SamplingCampaignRequestSectionName } from '@campaigns/constants/sampling/SamplingCampaignRequestSectionConstants';
import { ProductModalProvider } from '@campaigns/contexts/ProductModalContext';
import { brandBriefUploadStore } from '@campaigns/hooks/brandBriefUploadStore';
import { isCampaignAssetUploadInProgress } from '@campaigns/hooks/campaignAssetUploadState';
import { contentInspirationUploadStore } from '@campaigns/hooks/contentInspirationUploadStore';
import type { ContentInspirationUploadRecord } from '@campaigns/hooks/contentInspirationUploadTypes';
import { getIsAmbassadorTargetingEnabled } from '@campaigns/redux/selectors/getIsAmbassadorTargetingEnabled';
import { getIsCastaCrfQuoteEnabled } from '@campaigns/redux/selectors/getIsCastaCrfQuoteEnabled';
import { getIsSamplingCampaignSupportingResourceUploadEnabled } from '@campaigns/redux/selectors/getIsSamplingCampaignSupportingResourceUploadEnabled';
import { getIsSamplingCampaignsMultiSelectSocialEnabled } from '@campaigns/redux/selectors/getIsSamplingCampaignsMultiSelectSocialEnabled';
import { getIsSamplingEnhancedBriefAuthoringEnabled } from '@campaigns/redux/selectors/getIsSamplingEnhancedBriefAuthoringEnabled';
import { getIsSamplingPrepaidCampaignsEnabled } from '@campaigns/redux/selectors/getIsSamplingPrepaidCampaignsEnabled';
import {
  hasProductsWithMissingDetails,
  validateForRequest,
} from '@campaigns/utils/sampling/SamplingCampaignRequestValidationRulesUtils';
import {
  PrepaidConfirmationIssues,
  getCreatePrepaidConfirmationIssues,
} from '@campaigns/utils/sampling/SamplingPrepaidValidationUtils';
import { mergeProductsInCampaignRequest } from '@campaigns/utils/sampling/SamplingProductUtils';
import { useCurrentStateAndParams } from '@hooks/useCurrentStateAndParams';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import { ExternalQuoteId } from '@models/ExternalQuoteId';
import { QuoteId } from '@models/QuoteId';
import { navigateToState } from '@react-bridge/wrappers/navigation';
import { getCurrentUser } from '@redux/selectors/getSessionInfo';
import { isDefined, isTruthy } from '@utils/filterPredicates';
import { getRequestErrorCode } from '@utils/getRequestErrorCode';

import { RoutableStateName } from 'app/top-level-states';

export type RequestContactModalVariant = 'requestContact' | 'customQuote';

export const addInstagramPost = (
  campaign: SamplingCampaignRequest,
  isPrepaidCampaign: boolean,
  isMultiSelectSocialEnabled: boolean,
): SamplingCampaignRequest => {
  const socials = campaign.memberTasks?.socials;
  const instagramStory = socials?.find(
    ({ platform }) => platform === 'instagramStory',
  );
  const hasInstagramPost = socials?.some(
    ({ platform }) => platform === 'instagramPost',
  );

  if (
    !isPrepaidCampaign ||
    !isMultiSelectSocialEnabled ||
    campaign.status !== 'draft' ||
    !instagramStory ||
    hasInstagramPost
  ) {
    return campaign;
  }

  return {
    ...campaign,
    memberTasks: {
      ...campaign.memberTasks,
      socials: [
        ...(socials ?? []),
        {
          platform: 'instagramPost',
          handle: instagramStory.handle,
          hashtag: instagramStory.hashtag,
        },
      ],
    },
  };
};

export const getNewPurchaseCampaignRequest = (
  campaign: SamplingCampaignRequest,
): SamplingCampaignRequest => ({
  ...campaign,
  isNewPurchase: true,
  purchaseOption: [],
});
interface SamplingCampaignContextProps {
  formControl: Control<SamplingCampaignRequest>;
  isAutoSaving: boolean;
  isDeleteError: boolean;
  isDeleting: boolean;
  isEnhancedBriefAuthoringEnabled: boolean;
  isError: boolean;
  isChangePurchaseModalOpen: boolean;
  isPending: boolean;
  isRedemptionLocationModalOpen: boolean;
  isRequestContactModalOpen: boolean;
  isQuoting: boolean;
  isCreateQuoteError: boolean;
  isRequesting: boolean;
  isSaveError: boolean;
  isSaving: boolean;
  isSubmitErrorsModalOpen: boolean;
  isSupportingResourceUploadEnabled: boolean;
  isUploadingBrandBrief: boolean;
  isUploadingCampaignAssets: boolean;
  isUploadingContentInspiration: boolean;
  pendingBrandBriefFilename: string | undefined;
  pendingContentInspirationUploadCount: number;
  contentInspirationUploadRecords: ContentInspirationUploadRecord[];
  prepaidCampaignRequestIssues: PrepaidConfirmationIssues;
  redemptionLocationIndex: number | undefined;
  redemptionLocationToEdit: Retailer | undefined;
  samplingCampaignRequest: SamplingCampaignRequest | undefined;
  validationSubmitErrors: Map<SamplingCampaignRequestSectionName, string[]>;
  closeChangePurchaseModal: () => void;
  closeRedemptionLocationModal: () => void;
  closeSubmitErrorsModal: () => void;
  deleteLocation: (index: number) => void;
  handleAutoSave: (targetState: RoutableStateName) => void;
  handleDeleteCampaignRequest: () => Promise<void>;
  handleRequestCampaign: () => void;
  handleRequestContactCampaign: (
    quoteId?: QuoteId,
    externalQuoteId?: ExternalQuoteId,
  ) => Promise<void>;
  handleRequestQuote: () => Promise<void>;
  handleSubmitCampaign: () => Promise<void>;
  handleRevertToDraft: () => Promise<void>;
  handleSave: (
    shouldNavigate?: boolean,
    updatedCampaignRequest?: SamplingCampaignRequest,
  ) => Promise<boolean>;
  openBulkUploadModal: () => void;
  openChangePurchaseModal: () => void;
  openRedemptionLocationModal: (index?: number) => void;
  openRequestContactModal: (variant?: RequestContactModalVariant) => void;
  openPaymentSuccessModal: () => void;
  removeBrandBrief: () => void;
  requestContactModalVariant: RequestContactModalVariant;
  saveLocation: (data: Retailer) => void;
  startBrandBriefUpload: (file: File) => void;
  startContentInspirationUpload: (files: File[]) => void;
  watchFormValues: UseFormWatch<SamplingCampaignRequest>;
  updateFormValues: UseFormSetValue<SamplingCampaignRequest>;
  triggerFormValidation: UseFormTrigger<SamplingCampaignRequest>;
  canAddProductsToCatalog: boolean;
  selectedRetailer: ClientRetailerWithId | undefined;
}

const defaultContext: SamplingCampaignContextProps = {
  canAddProductsToCatalog: false,
  isAutoSaving: false,
  isDeleteError: false,
  isDeleting: false,
  isEnhancedBriefAuthoringEnabled: false,
  isChangePurchaseModalOpen: false,
  isError: false,
  isPending: false,
  isRedemptionLocationModalOpen: false,
  isRequestContactModalOpen: false,
  isQuoting: false,
  isCreateQuoteError: false,
  isRequesting: false,
  isSaveError: false,
  isSaving: false,
  isSubmitErrorsModalOpen: false,
  isSupportingResourceUploadEnabled: false,
  isUploadingBrandBrief: false,
  isUploadingCampaignAssets: false,
  isUploadingContentInspiration: false,
  pendingBrandBriefFilename: undefined,
  pendingContentInspirationUploadCount: 0,
  contentInspirationUploadRecords: [],
  prepaidCampaignRequestIssues: { purchaseOptionIds: [], rows: [] },
  redemptionLocationIndex: undefined,
  redemptionLocationToEdit: undefined,
  samplingCampaignRequest: undefined,
  selectedRetailer: undefined,
  validationSubmitErrors: new Map(),
  requestContactModalVariant: 'requestContact',
  closeChangePurchaseModal: () => undefined,
  closeRedemptionLocationModal: () => undefined,
  closeSubmitErrorsModal: () => undefined,
  deleteLocation: () => undefined,
  handleAutoSave: () => undefined,
  handleDeleteCampaignRequest: async () => Promise.resolve(),
  handleRequestCampaign: () => undefined,
  handleRequestContactCampaign: async (
    _quoteId?: QuoteId,
    _externalQuoteId?: ExternalQuoteId,
  ) => Promise.resolve(),
  handleRequestQuote: async () => Promise.resolve(),
  handleSubmitCampaign: async () => Promise.resolve(),
  handleRevertToDraft: async () => Promise.resolve(),
  handleSave: async () => Promise.resolve(false),
  openBulkUploadModal: () => undefined,
  openChangePurchaseModal: () => undefined,
  openRedemptionLocationModal: () => undefined,
  openRequestContactModal: () => undefined,
  openPaymentSuccessModal: () => undefined,
  removeBrandBrief: () => undefined,
  saveLocation: () => undefined,
  startBrandBriefUpload: () => undefined,
  startContentInspirationUpload: () => undefined,
  formControl: {} as Control<SamplingCampaignRequest>,
  watchFormValues: (() =>
    undefined) as unknown as UseFormWatch<SamplingCampaignRequest>,
  updateFormValues: () => undefined,
  triggerFormValidation: async () => false,
};

const SamplingCampaignContext =
  createContext<SamplingCampaignContextProps>(defaultContext);

export const SamplingCampaignProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  // Hooks
  // TODO: PD-326922 - Remove feature check once SamplingEnhancedBriefAuthoring is released.
  const isEnhancedBriefAuthoringEnabled = useSelector(
    getIsSamplingEnhancedBriefAuthoringEnabled,
  );
  // TODO: PD-326922 - Remove feature check once SamplingCampaignSupportingResourceUpload is released.
  const isSupportingResourceUploadEnabled = useSelector(
    getIsSamplingCampaignSupportingResourceUploadEnabled,
  );
  const { params, state } =
    useCurrentStateAndParams<SamplingCampaignRequestParams>();
  //TO-DO: PD-326004 - Remove the isMultiSelectSocialEnabled feature flag once it is rolled out to all clients.
  const isMultiSelectSocialEnabled = useSelector(
    getIsSamplingCampaignsMultiSelectSocialEnabled,
  );
  const { data, error, isError, isPending } = useSamplingCampaignRequestQuery(
    {
      campaignId: params.campaignId,
    },
    (data) => {
      const campaign = addInstagramPost(
        data,
        state.startsWith('campaigns.sampling-prepaid-'),
        isMultiSelectSocialEnabled,
      );
      return {
        ...campaign,
        targetClientId: isDefined(campaign.requestorClientId)
          ? campaign.bvClientId
          : undefined,
      };
    },
  );
  const isAmbassadorTargetingEnabled = useSelector(
    getIsAmbassadorTargetingEnabled,
  );
  useSearchVibeCommunitiesQuery(isAmbassadorTargetingEnabled);

  // State
  const [redemptionLocationToEdit, setRedemptionLocationToEdit] = useState<
    Retailer | undefined
  >(undefined);
  const [redemptionLocationIndex, setRedemptionLocationIndex] = useState<
    number | undefined
  >(undefined);
  const [
    isRequestCampaignModalSuccessOpen,
    setIsRequestCampaignModalSuccessOpen,
  ] = useState(false);
  const [
    requestCampaignModalSuccessVariant,
    setRequestCampaignModalSuccessVariant,
  ] = useState<RequestCampaignSuccessModalVariant>('submitted');
  const [isPaymentSuccessModalOpen, setIsPaymentSuccessModalOpen] =
    useState(false);
  const [
    isPrepaidCampaignRequestModalOpen,
    setIsPrepaidCampaignRequestModalOpen,
  ] = useState(false);
  const [prepaidCampaignRequestIssues, setPrepaidCampaignRequestIssues] =
    useState<PrepaidConfirmationIssues>({ purchaseOptionIds: [], rows: [] });
  const [isChangePurchaseModalOpen, setIsChangePurchaseModalOpen] =
    useState(false);
  const [isRequestCampaignModalOpen, setIsRequestCampaignModalOpen] =
    useState(false);
  const [
    isRequestNewPurchaseFromPrepaidOpen,
    setIsRequestNewPurchaseFromPrepaidOpen,
  ] = useState(false);
  const [isRequestContactModalOpen, setIsRequestContactModalOpen] =
    useState(false);
  const [requestContactModalVariant, setRequestContactModalVariant] =
    useState<RequestContactModalVariant>('requestContact');
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const [isRedemptionLocationModalOpen, setIsRedemptionLocationModalOpen] =
    useState(false);
  const [isSubmitErrorsModalOpen, setIsSubmitErrorsModalOpen] = useState(false);
  const [validationSubmitErrors, setSubmitValidationErrors] = useState<
    Map<SamplingCampaignRequestSectionName, string[]>
  >(new Map());
  const [isSaving, setIsSaving] = useState(false);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false); // TODO: PD-251368 - [FE] Implement section check before request
  const [isQuoting, setIsQuoting] = useState(false);
  const [isCreateQuoteError, setIsCreateQuoteError] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const contentInspirationUploadState = useSyncExternalStore(
    contentInspirationUploadStore.subscribe,
    () => contentInspirationUploadStore.getState(data?.campaignRequestId),
  );
  const {
    contentInspirationUploadRecords,
    pendingContentInspirationUploadCount,
  } = contentInspirationUploadState;
  const isUploadingContentInspiration =
    pendingContentInspirationUploadCount > 0;
  const brandBriefUploadState = useSyncExternalStore(
    brandBriefUploadStore.subscribe,
    () => brandBriefUploadStore.getState(data?.campaignRequestId),
  );
  const { pendingBrandBriefFilename } = brandBriefUploadState;
  const isUploadingBrandBrief = isDefined(pendingBrandBriefFilename);
  const isUploadingCampaignAssets =
    isUploadingContentInspiration || isUploadingBrandBrief;
  useEffect(() => {
    const campaignRequestId = data?.campaignRequestId;
    return () => {
      if (isDefined(campaignRequestId)) {
        contentInspirationUploadStore.clearStateWhenIdle(campaignRequestId);
        brandBriefUploadStore.clearStateWhenIdle(campaignRequestId);
      }
    };
  }, [data?.campaignRequestId]);
  const redemptionLocationFormMethods = useForm<Retailer>({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: {},
  });

  const { isError: isSaveError, mutateAsync: editCampaignRequestAsync } =
    useEditClientCampaignRequestMutation();

  const { isError: isDeleteError, mutateAsync: deleteCampaignRequestAsync } =
    useDeleteClientCampaignRequestMutation();

  const { mutateAsync: uploadInspirationImage } =
    useUploadInspirationImageMutation();
  const { mutateAsync: uploadInspirationVideo } =
    useUploadInspirationVideoMutation();
  const { mutateAsync: uploadBrandBrief } = useUploadBrandBriefMutation();
  const { mutateAsync: requestQuoteAsync } = useRequestQuoteMutation();

  const { mutate: patchQuoteStatusMutate } = usePatchQuoteStatusMutation();

  const dispatch = useDispatch();
  const { email, firstName, lastName } = useSelector(getCurrentUser);

  const showCampaignAssetUploadWarning = () => {
    dispatch(
      toastNotificationsActions.addToastNotification({
        message:
          'Please wait for uploads to finish before saving or navigating.',
        options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        title: 'Upload In Progress',
        type: ToastType.ERROR,
      }),
    );
  };

  // TODO: PD-304383 - Remove feature flag check once CastaCrfQuote is released
  const isCastaCrfQuoteEnabled = useSelector(getIsCastaCrfQuoteEnabled);

  // TODO: PD-302554 - Delete feature flag and leave only prepaid campaign workflow
  const isSamplingPrepaidCampaignsEnabled = useSelector(
    getIsSamplingPrepaidCampaignsEnabled,
  );

  const formMethods = useForm<SamplingCampaignRequest>({
    defaultValues: data,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  const { control, getValues, reset, setValue, watch } = formMethods;

  const getNewPurchaseFormValues = () => {
    const formValues = getNewPurchaseCampaignRequest(getValues());
    setValue('isNewPurchase', formValues.isNewPurchase);
    setValue('purchaseOption', formValues.purchaseOption);
    return formValues;
  };

  const assetSaveQueueRef = useRef(Promise.resolve());
  const enqueueAssetSave = (save: () => Promise<void>) => {
    const savePromise = assetSaveQueueRef.current.then(save, save);
    assetSaveQueueRef.current = savePromise.catch(() => undefined);
    return savePromise;
  };

  const getSelectedRetailer = (
    retailersResponse: GetClientRetailersResponse | undefined,
  ): ClientRetailerWithId | undefined => {
    const { requestorClientId, targetClientId } = getValues();

    if (
      !isDefined(retailersResponse?.results) ||
      !isDefined(requestorClientId) ||
      !isDefined(targetClientId)
    ) {
      return undefined;
    }

    // Find the retailer whose client ID matches the target ID from the form.
    return retailersResponse.results.find(
      (retailer) => retailer.bvClientId === targetClientId,
    );
  };

  const { data: selectedRetailer } =
    useGetClientRetailersQuery(getSelectedRetailer);

  const canAddProductsToCatalog = selectedRetailer
    ? isTruthy(selectedRetailer.productCreationEnabled)
    : true;

  useEffect(() => {
    if (isError) {
      const errorCode = getRequestErrorCode(error);
      if (errorCode === 404 || errorCode === 403) {
        void navigateToState('campaigns.sampling');
      }
    }
  }, [isError, error]);

  useEffect(() => {
    if (data) {
      reset(data);
    }
  }, [data, reset]);

  const startContentInspirationUpload = (files: File[]) => {
    const formValues = getValues();
    const campaignRequestId = formValues.campaignRequestId;
    if (
      !isDefined(campaignRequestId) ||
      isUploadingCampaignAssets ||
      isCampaignAssetUploadInProgress(campaignRequestId) ||
      isSaving ||
      isAutoSaving
    ) {
      return;
    }

    const uploadRecords: ContentInspirationUploadRecord[] = files.map(
      (file, index) => ({
        id: `${file.name}-${Date.now()}-${index}-${Math.random()
          .toString(36)
          .slice(2, 8)}`,
        filename: file.name,
        type: isSupportedMimeType(
          file.type,
          CONTENT_INSPIRATION_VIDEO_MIME_TYPES,
        )
          ? 'video'
          : 'image',
        status: 'pending',
      }),
    );
    const currentUploadState =
      contentInspirationUploadStore.getState(campaignRequestId);
    contentInspirationUploadStore.setState(campaignRequestId, {
      contentInspirationUploadRecords: [
        ...currentUploadState.contentInspirationUploadRecords,
        ...uploadRecords,
      ],
      pendingContentInspirationUploadCount:
        currentUploadState.pendingContentInspirationUploadCount + files.length,
    });
    void Promise.allSettled(
      files.map(async (file, index) => {
        const record = uploadRecords[index];
        if (!isDefined(record)) {
          throw new Error('Missing upload record for selected file.');
        }
        const isVideo = isSupportedMimeType(
          file.type,
          CONTENT_INSPIRATION_VIDEO_MIME_TYPES,
        );
        return {
          isVideo,
          recordId: record.id,
          url: isVideo
            ? await uploadInspirationVideo({ campaignRequestId, file })
            : await uploadInspirationImage({ campaignRequestId, file }),
        };
      }),
    ).then((uploadResults) => {
      const uploadedMedia = uploadResults
        .filter(
          (
            result,
          ): result is PromiseFulfilledResult<{
            isVideo: boolean;
            recordId: string;
            url: string;
          }> => result.status === 'fulfilled',
        )
        .map((result) => result.value);
      const uploadedImageUrls = uploadedMedia
        .filter((media) => !media.isVideo)
        .map((media) => media.url);
      const uploadedVideoUrls = uploadedMedia
        .filter((media) => media.isVideo)
        .map((media) => media.url);

      const uploadedMediaByRecordId = new Map(
        uploadedMedia.map((media) => [media.recordId, media.url]),
      );
      contentInspirationUploadStore.setState(campaignRequestId, {
        contentInspirationUploadRecords: contentInspirationUploadStore
          .getState(campaignRequestId)
          .contentInspirationUploadRecords.map((record) => ({
            ...record,
            uploadedUrl: uploadedMediaByRecordId.has(record.id)
              ? uploadedMediaByRecordId.get(record.id)
              : record.uploadedUrl,
          })),
      });

      const failedUploads = uploadResults.filter(
        (result): result is PromiseRejectedResult =>
          result.status === 'rejected',
      );

      const currentCampaign = getValues();
      let didSaveUploadedMedia = false;
      let contentExampleImageUrls =
        currentCampaign.contentExampleImageUrls ?? [];
      let contentExampleVideoUrls =
        currentCampaign.contentExampleVideoUrls ?? [];
      const saveUploadedMedia = async () => {
        if (uploadedImageUrls.length === 0 && uploadedVideoUrls.length === 0) {
          return;
        }
        // Read latest form snapshot right before saving so edits made while
        // uploads were in flight are not overwritten by stale form values.
        const latestCampaign = getValues();
        if (
          !latestCampaign.memberTasks?.postOnSocial ||
          !latestCampaign.memberTasks.socials?.length
        ) {
          return;
        }
        contentExampleImageUrls = [
          ...new Set([
            ...(latestCampaign.contentExampleImageUrls ?? []),
            ...uploadedImageUrls,
          ]),
        ];
        contentExampleVideoUrls = [
          ...new Set([
            ...(latestCampaign.contentExampleVideoUrls ?? []),
            ...uploadedVideoUrls,
          ]),
        ];
        await editCampaignRequestAsync({
          id: campaignRequestId,
          campaign: {
            ...latestCampaign,
            contentExampleImageUrls,
            contentExampleVideoUrls,
          },
        });
        didSaveUploadedMedia = true;
      };

      void saveUploadedMedia()
        .then(() => {
          if (didSaveUploadedMedia) {
            dispatch(
              toastNotificationsActions.addToastNotification({
                message:
                  uploadedMedia.length === 1
                    ? 'Content inspiration uploaded successfully.'
                    : `${uploadedMedia.length} content inspiration items uploaded successfully.`,
                options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
                title: 'Upload Complete',
                type: ToastType.SUCCESS,
              }),
            );
          }
        })
        .catch(() => {
          dispatch(
            toastNotificationsActions.addToastNotification({
              message:
                'Content inspiration upload completed, but saving campaign changes failed. Please try again.',
              options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
              title: 'Upload Failed',
              type: ToastType.ERROR,
            }),
          );
        })
        .finally(() => {
          const storeState =
            contentInspirationUploadStore.getState(campaignRequestId);
          const failedRecordIds = uploadResults
            .map((result, index) => {
              const record = uploadRecords[index];
              return result.status === 'rejected' && isDefined(record)
                ? record.id
                : undefined;
            })
            .filter(isDefined);
          const uploadedRecordIds = new Set(
            uploadedMedia.map((media) => media.recordId),
          );
          const getCompletedUploadStatus = (
            record: ContentInspirationUploadRecord,
          ) => {
            if (uploadedRecordIds.has(record.id)) {
              if (didSaveUploadedMedia) {
                return 'success';
              }
              return 'error';
            }
            if (failedRecordIds.includes(record.id)) {
              return 'error';
            }
            return record.status;
          };
          const completedRecords =
            storeState.contentInspirationUploadRecords.map((record) => ({
              ...record,
              status: getCompletedUploadStatus(record),
            }));

          if (didSaveUploadedMedia) {
            setValue('contentExampleImageUrls', contentExampleImageUrls);
            setValue('contentExampleVideoUrls', contentExampleVideoUrls);
          }

          const pendingUploadCount = Math.max(
            storeState.pendingContentInspirationUploadCount - files.length,
            0,
          );
          contentInspirationUploadStore.setState(campaignRequestId, {
            contentInspirationUploadRecords:
              pendingUploadCount === 0
                ? completedRecords.filter(
                    (record) => record.status === 'pending',
                  )
                : completedRecords,
            pendingContentInspirationUploadCount: pendingUploadCount,
          });

          if (failedUploads.length > 0) {
            dispatch(
              toastNotificationsActions.addToastNotification({
                message:
                  failedUploads.length === 1
                    ? 'One content inspiration upload failed. Please try again.'
                    : `${failedUploads.length} content inspiration uploads failed. Please try again.`,
                options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
                title: 'Upload Failed',
                type: ToastType.ERROR,
              }),
            );
          }
        });
    });
  };

  const startBrandBriefUpload = (file: File) => {
    const campaign = getValues();
    const campaignRequestId = campaign.campaignRequestId;
    if (
      !isDefined(campaignRequestId) ||
      isUploadingCampaignAssets ||
      isCampaignAssetUploadInProgress(campaignRequestId) ||
      isSaving ||
      isAutoSaving
    ) {
      return;
    }

    brandBriefUploadStore.setState(campaignRequestId, {
      pendingBrandBriefFilename: file.name,
    });

    let hasUploadedBrandBrief = false;
    void uploadBrandBrief({ campaignRequestId, file })
      .then(async (uploadedUrl) => {
        hasUploadedBrandBrief = true;
        if (getValues().campaignRequestId !== campaignRequestId) {
          return false;
        }

        await enqueueAssetSave(async () => {
          const latestCampaign = getValues();
          if (latestCampaign.campaignRequestId !== campaignRequestId) {
            return;
          }
          await editCampaignRequestAsync({
            id: campaignRequestId,
            campaign: {
              ...latestCampaign,
              brandBriefUrl: uploadedUrl,
            },
          });
          setValue('brandBriefUrl', uploadedUrl);
        });
        if (getValues().campaignRequestId !== campaignRequestId) {
          return false;
        }
        return true;
      })
      .then((didSaveBrandBrief) => {
        if (!didSaveBrandBrief) {
          return;
        }
        dispatch(
          toastNotificationsActions.addToastNotification({
            message: 'Document uploaded successfully.',
            options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
            title: 'Upload Complete',
            type: ToastType.SUCCESS,
          }),
        );
      })
      .catch(() => {
        dispatch(
          toastNotificationsActions.addToastNotification({
            message: hasUploadedBrandBrief
              ? 'Document uploaded, but saving campaign changes failed. Please try again.'
              : 'Failed to upload document. Please try again.',
            options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
            title: 'Upload Failed',
            type: ToastType.ERROR,
          }),
        );
      })
      .finally(() => {
        brandBriefUploadStore.setState(campaignRequestId, {
          pendingBrandBriefFilename: undefined,
        });
      });
  };

  const removeBrandBrief = () => {
    const campaignRequestId = getValues().campaignRequestId;
    if (!isDefined(campaignRequestId)) {
      return;
    }

    setValue('brandBriefUrl', '');
  };

  // Handlers
  const closeSubmitErrorsModal = () => {
    setIsSubmitErrorsModalOpen(false);
  };

  const handleRevertToDraft = async () => {
    if (isUploadingCampaignAssets) {
      showCampaignAssetUploadWarning();
      return;
    }
    const formValues = getValues();
    if (formValues.campaignRequestId) {
      setIsSaving(true);
      try {
        await editCampaignRequestAsync({
          id: formValues.campaignRequestId,
          campaign: {
            ...formValues,
            status: 'draft',
          },
        });
        const targetState = isSamplingPrepaidCampaignsEnabled
          ? 'campaigns.sampling-prepaid-campaign-request-details-edit'
          : 'campaigns.sampling-campaign-request-details-edit';

        void navigateToState(targetState, {
          campaignId: formValues.campaignRequestId,
        });
      } catch (error) {
        // Error will be handled by isSaveError state
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleDeleteCampaignRequest = async () => {
    if (isUploadingCampaignAssets) {
      showCampaignAssetUploadWarning();
      return;
    }
    const formValues = getValues();
    if (formValues.campaignRequestId) {
      setIsDeleting(true);

      try {
        await deleteCampaignRequestAsync({
          id: formValues.campaignRequestId,
        });
        dispatch(
          toastNotificationsActions.addToastNotification({
            message: 'The campaign request was successfully deleted.',
            options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
            title: 'Campaign Request Deleted',
            type: ToastType.DELETE,
          }),
        );
        void navigateToState('campaigns.sampling');
      } catch (error) {
        // Error will be handled by isDeleteError state
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const executeAutoSave = async (
    targetState: RoutableStateName,
    updatedValues?: Partial<SamplingCampaignRequest>,
  ) => {
    if (isUploadingCampaignAssets) {
      showCampaignAssetUploadWarning();
      return;
    }

    const formValues = { ...getValues(), ...updatedValues };
    if (formValues.campaignRequestId) {
      setIsAutoSaving(true);
      try {
        const isValid = await formMethods.trigger();
        const productErrors = hasProductsWithMissingDetails(formValues);

        if (!isValid || productErrors.size > 0) {
          return;
        }

        await editCampaignRequestAsync({
          id: formValues.campaignRequestId,
          campaign: formValues,
        });

        void navigateToState(targetState, {
          campaignId: formValues.campaignRequestId,
        });
      } catch (error) {
        // Error will be handled by isSaveError state
      } finally {
        setIsAutoSaving(false);
      }
    }
  };

  const handleAutoSave = async (targetState: RoutableStateName) => {
    await executeAutoSave(targetState);
  };

  const handleCreatePrepaidNewPurchaseConfirm = async () => {
    const formValues = getNewPurchaseFormValues();
    if (formValues.campaignRequestId) {
      setIsRequesting(true);
      try {
        await editCampaignRequestAsync({
          id: formValues.campaignRequestId,
          campaign: {
            ...formValues,
            status: 'submitted',
          },
        });
        setIsPrepaidCampaignRequestModalOpen(false);
        setIsRequestNewPurchaseFromPrepaidOpen(false);
        setPrepaidCampaignRequestIssues({ purchaseOptionIds: [], rows: [] });
        setIsRequestCampaignModalSuccessOpen(true);
      } catch (error) {
        // Error will be handled by isSaveError state
      } finally {
        setIsRequesting(false);
      }
    }
  };

  const handleCreatePrepaidNewPurchaseCancel = () => {
    setIsPrepaidCampaignRequestModalOpen(false);
    setIsRequestNewPurchaseFromPrepaidOpen(false);
    setPrepaidCampaignRequestIssues({ purchaseOptionIds: [], rows: [] });
  };

  const handlePrepaidContinueRequestNewPurchase = () => {
    setIsPrepaidCampaignRequestModalOpen(false);
    setIsRequestNewPurchaseFromPrepaidOpen(true);
  };

  const handlePrepaidContinueChangePurchaseOption = () => {
    setIsPrepaidCampaignRequestModalOpen(false);
    openChangePurchaseModal();
  };

  const handleSave = async (
    shouldNavigate: boolean = true,
  ): Promise<boolean> => {
    if (isUploadingCampaignAssets) {
      showCampaignAssetUploadWarning();
      return false;
    }

    const formValues = getValues();
    const campaignRequestId = formValues.campaignRequestId;

    if (!isDefined(campaignRequestId)) {
      return false;
    }

    const productErrors = hasProductsWithMissingDetails(formValues);
    if (productErrors.size > 0) {
      setSubmitValidationErrors(productErrors);
      setIsSubmitErrorsModalOpen(true);
      return false;
    }
    setIsSaving(true);
    try {
      await editCampaignRequestAsync({
        id: campaignRequestId,
        campaign: {
          ...formValues,
          comments: formValues.comments ?? {},
        },
      });
      if (shouldNavigate) {
        void navigateToState('campaigns.sampling');
      }
      return true;
    } catch (error) {
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleRequestCampaign = () => {
    if (isUploadingCampaignAssets) {
      showCampaignAssetUploadWarning();
      return;
    }
    const formValues = getValues();
    if (formValues.campaignRequestId) {
      const errors = validateForRequest(
        formValues,
        isEnhancedBriefAuthoringEnabled,
      );
      if (errors.size > 0) {
        setSubmitValidationErrors(errors);
        setIsSubmitErrorsModalOpen(true);
        return;
      }

      if (isSamplingPrepaidCampaignsEnabled) {
        const issues = getCreatePrepaidConfirmationIssues(
          formValues,
          isSamplingPrepaidCampaignsEnabled,
        );
        if (!issues.rows.length) {
          setIsRequestCampaignModalOpen(true);
          return;
        }
        setPrepaidCampaignRequestIssues(issues);
        setIsPrepaidCampaignRequestModalOpen(true);
        return;
      }

      setIsRequestCampaignModalOpen(true);
    }
  };

  const handleRequestQuote = async () => {
    if (isUploadingCampaignAssets) {
      showCampaignAssetUploadWarning();
      return;
    }
    const formValues = getNewPurchaseFormValues();
    if (formValues.campaignRequestId) {
      const errors = validateForRequest(
        formValues,
        isEnhancedBriefAuthoringEnabled,
      );
      if (errors.size > 0) {
        setSubmitValidationErrors(errors);
        setIsSubmitErrorsModalOpen(true);
      } else {
        setIsQuoting(true);
        setIsCreateQuoteError(false);

        try {
          await editCampaignRequestAsync({
            id: formValues.campaignRequestId,
            campaign: {
              ...formValues,
              comments: formValues.comments ?? {},
            },
          });

          await requestQuoteAsync({
            campaignRequestId: formValues.campaignRequestId,
            payload: {
              quoteContact: {
                email,
                firstName,
                lastName,
              },
            },
          });

          void navigateToState(
            'campaigns.sampling-campaign-request-details-quote',
            {
              campaignId: formValues.campaignRequestId,
            },
          );
        } catch (error) {
          setIsCreateQuoteError(true);
          // Error will be handled by isSaveError state
        } finally {
          setIsQuoting(false);
        }
      }
    }
  };

  const handleSubmitCampaign = async () => {
    if (isUploadingCampaignAssets) {
      return;
    }
    const formValues = getValues();
    if (formValues.campaignRequestId) {
      setIsRequesting(true);

      try {
        await editCampaignRequestAsync({
          id: formValues.campaignRequestId,
          campaign: {
            ...formValues,
            status: 'submitted',
          },
        });

        setIsRequestCampaignModalOpen(false);
        setIsPrepaidCampaignRequestModalOpen(false);
        setPrepaidCampaignRequestIssues({ purchaseOptionIds: [], rows: [] });
        setIsRequestCampaignModalSuccessOpen(true);
      } catch (error) {
        // Error will be handled by isSaveError state
      } finally {
        setIsRequesting(false);
      }
    }
  };

  const handleRequestContactCampaign = async (
    quoteId?: QuoteId,
    externalQuoteId?: ExternalQuoteId,
  ) => {
    if (isUploadingCampaignAssets) {
      return;
    }
    const formValues = getValues();
    if (formValues.campaignRequestId) {
      setIsRequesting(true);
      try {
        await editCampaignRequestAsync({
          id: formValues.campaignRequestId,
          campaign: {
            ...formValues,
            quoteId,
            externalQuoteId,
            status: 'submitted',
          },
        });
        setIsRequestContactModalOpen(false);
        setRequestCampaignModalSuccessVariant('contact_requested');
        setIsRequestCampaignModalSuccessOpen(true);

        // Patch quote status as best-effort after the primary submit succeeds.
        // Failure here does not affect the user-facing success state.
        if (isDefined(quoteId)) {
          patchQuoteStatusMutate({
            quoteId,
            payload: { status: QuoteState.CONTACT_REQUESTED },
          });
        }
      } catch (error) {
        // Error will be handled by isSaveError state
      } finally {
        setIsRequesting(false);
      }
    }
  };

  const saveLocation = (data: Retailer) => {
    const formValues = getValues();
    const updatedRetailers = [
      ...(formValues.fulfillmentOptions?.retailers ?? []),
    ];
    if (redemptionLocationIndex !== undefined) {
      updatedRetailers[redemptionLocationIndex] = data;
    } else {
      updatedRetailers.push(data);
    }
    setValue('fulfillmentOptions.retailers', updatedRetailers);
  };

  const deleteLocation = (index: number) => {
    const formValues = getValues();
    const updatedRetailers = [
      ...(formValues.fulfillmentOptions?.retailers ?? []),
    ].filter((_, i) => i !== index);
    setValue('fulfillmentOptions.retailers', updatedRetailers);
  };

  const addProductToCampaign = (
    product: BVSamplingProduct,
    isEdit: boolean,
    originalProductId?: string,
  ) => {
    const currentProducts = getValues().products ?? [];
    const updatedProducts = isEdit
      ? currentProducts.map((p) =>
          p.targetCatalogProductId ===
          (originalProductId ?? product.targetCatalogProductId)
            ? {
                ...product,
                variations: product.variations?.map((variation) => {
                  const existingVariation = p.variations?.find(
                    (productVariation) =>
                      productVariation.logisticsRef === variation.logisticsRef,
                  );
                  if (existingVariation?.quantity === variation.quantity) {
                    return variation;
                  }

                  return {
                    ...variation,
                    allocations: undefined,
                  };
                }),
              }
            : p,
        )
      : [...currentProducts, product];
    setValue('products', updatedProducts);
  };

  const openRedemptionLocationModal = (index?: number) => {
    const formValues = getValues();
    setIsRedemptionLocationModalOpen(true);
    const retailer =
      isDefined(index) && formValues.fulfillmentOptions?.retailers
        ? formValues.fulfillmentOptions.retailers[index]
        : undefined;
    setRedemptionLocationToEdit(retailer);
    setRedemptionLocationIndex(index);
    redemptionLocationFormMethods.reset(retailer);
  };

  const openBulkUploadModal = () => {
    setIsBulkUploadModalOpen(true);
  };

  const openChangePurchaseModal = () => {
    setIsChangePurchaseModalOpen(true);
  };

  const openRequestContactModal = (
    variant: RequestContactModalVariant = 'requestContact',
  ) => {
    setRequestContactModalVariant(variant);
    setIsRequestContactModalOpen(true);

    const formValues = getValues();
    if (!isDefined(formValues.keyContact)) {
      setValue('keyContact', {
        email: formValues.formOwnerEmail ?? '',
        name: formValues.formOwner ?? '',
      });
    }
  };

  const closeChangePurchaseModal = () => {
    setIsChangePurchaseModalOpen(false);
  };

  const closeRedemptionLocationModal = () => {
    setIsRedemptionLocationModalOpen(false);
    setRedemptionLocationToEdit(undefined);
    redemptionLocationFormMethods.reset({});
  };

  const closeRequestCampaignSuccessModal = () => {
    setIsRequestCampaignModalSuccessOpen(false);
    setRequestCampaignModalSuccessVariant('submitted');
    const campaignRequestId = getValues().campaignRequestId;

    if (isDefined(campaignRequestId)) {
      // Navigate when user closes success modal
      const targetState = isSamplingPrepaidCampaignsEnabled
        ? 'campaigns.sampling-prepaid-campaign-request-details'
        : 'campaigns.sampling-campaign-request-details';

      void navigateToState(targetState, {
        campaignId: campaignRequestId,
      });
    }
  };

  const closePaymentSuccessModal = () => {
    setIsPaymentSuccessModalOpen(false);
    const campaignRequestId = getValues().campaignRequestId;

    if (isDefined(campaignRequestId)) {
      void navigateToState(
        'campaigns.sampling-prepaid-campaign-request-details',
        {
          campaignId: campaignRequestId,
        },
      );
    }
  };

  const handleProductsImported = (newProducts: BVSamplingProduct[]) => {
    const formValues = getValues();
    const mergedProducts = mergeProductsInCampaignRequest(
      formValues.products,
      newProducts,
    );
    setValue('products', mergedProducts);
  };

  return (
    <SamplingCampaignContext.Provider
      value={{
        formControl: control,
        isAutoSaving,
        isChangePurchaseModalOpen,
        isDeleteError,
        isDeleting,
        isEnhancedBriefAuthoringEnabled,
        isError,
        isPending,
        isRedemptionLocationModalOpen,
        isRequestContactModalOpen,
        isQuoting,
        isCreateQuoteError,
        isRequesting,
        isSaveError: isSaveError || isCreateQuoteError,
        isSaving,
        isSubmitErrorsModalOpen,
        isSupportingResourceUploadEnabled,
        isUploadingBrandBrief,
        isUploadingCampaignAssets,
        isUploadingContentInspiration,
        pendingBrandBriefFilename,
        pendingContentInspirationUploadCount,
        contentInspirationUploadRecords,
        prepaidCampaignRequestIssues,
        redemptionLocationIndex,
        redemptionLocationToEdit,
        samplingCampaignRequest: watch(),
        validationSubmitErrors,
        requestContactModalVariant,
        closeChangePurchaseModal,
        closeRedemptionLocationModal,
        closeSubmitErrorsModal,
        deleteLocation,
        handleAutoSave,
        handleDeleteCampaignRequest,
        handleRequestCampaign,
        handleRequestContactCampaign,
        handleRequestQuote,
        handleSubmitCampaign,
        handleRevertToDraft,
        handleSave,
        openBulkUploadModal,
        openChangePurchaseModal,
        openRedemptionLocationModal,
        openRequestContactModal,
        openPaymentSuccessModal: () => setIsPaymentSuccessModalOpen(true),
        removeBrandBrief,
        saveLocation,
        triggerFormValidation: formMethods.trigger,
        startBrandBriefUpload,
        startContentInspirationUpload,
        updateFormValues: setValue,
        watchFormValues: watch,
        canAddProductsToCatalog,
        selectedRetailer,
      }}
    >
      <ProductModalProvider addProductToCampaign={addProductToCampaign}>
        <FormProvider {...formMethods}>
          <SamplingRequestSubmitErrorsModal />
          <FormProvider {...redemptionLocationFormMethods}>
            <CreateRedemptionLocationModal />
          </FormProvider>
          <RequestCampaignModal
            isOpen={isRequestCampaignModalOpen}
            onClose={() => setIsRequestCampaignModalOpen(false)}
            onSubmit={handleSubmitCampaign}
          />
          <RequestContactModal
            isOpen={isRequestContactModalOpen}
            onClose={() => setIsRequestContactModalOpen(false)}
          />
          <RequestCampaignSuccessModal
            isOpen={isRequestCampaignModalSuccessOpen}
            onClose={closeRequestCampaignSuccessModal}
            variant={requestCampaignModalSuccessVariant}
          />
          <PaymentSuccessModal
            isOpen={isPaymentSuccessModalOpen}
            onClose={closePaymentSuccessModal}
          />
          <BulkUploadModal
            closeModal={() => setIsBulkUploadModalOpen(false)}
            isModalOpen={isBulkUploadModalOpen}
            onProductsImported={handleProductsImported}
          />
          <ChangePurchaseOptionModal
            isLoading={isSaving}
            isModalOpen={isChangePurchaseModalOpen}
            onClose={() => setIsChangePurchaseModalOpen(false)}
          />
          <RequestNewPurchaseCampaignModal
            isOpen={isRequestNewPurchaseFromPrepaidOpen}
            onClose={() => setIsRequestNewPurchaseFromPrepaidOpen(false)}
            onSubmit={
              isCastaCrfQuoteEnabled
                ? handleRequestQuote
                : handleCreatePrepaidNewPurchaseConfirm
            }
          />
          <RequestPrepaidCampaignModal
            closeModal={handleCreatePrepaidNewPurchaseCancel}
            isModalOpen={isPrepaidCampaignRequestModalOpen}
            isRequesting={isRequesting}
            isSaveError={isSaveError}
            issues={prepaidCampaignRequestIssues}
            onCancel={handleCreatePrepaidNewPurchaseCancel}
            onChangePurchaseOption={handlePrepaidContinueChangePurchaseOption}
            onRequestNewPurchase={handlePrepaidContinueRequestNewPurchase}
          />
          {children}
        </FormProvider>
      </ProductModalProvider>
    </SamplingCampaignContext.Provider>
  );
};

export const useSamplingCampaign = () => useContext(SamplingCampaignContext);
